import { useState } from 'react'

import { Box, Checkbox, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useReconciliationTransactionDataTable,
} from 'generated/resources/datatable.graphql'
import { BankingTransactionFilter } from 'generated/resources/graphql'
import { sum } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ReconciliationItem } from 'modules/banking/hooks/useReconciliationForm'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

interface Props {
  onSelect: (value: ReconciliationItem) => void
  onRemove: (item: ReconciliationItem) => void
  isSelected: (id: number) => boolean
  currency?: string | null
  disabled: boolean
}

export interface TransactionDataTableFields {
  id: number
  bookingDate: string
  fullyReconciledAt?: string | null
  description?: string | null
  amountCents: number
  currency: string
  reconciliations: {
    __typename?: 'BankingReconciliation'
    id: number
    transactionReconciledAmountCents: number
  }[]
}

const getPendingAmount = (transaction: TransactionDataTableFields) => {
  const totalReconciledAmountCents =
    sum(transaction.reconciliations.map((r) => r.transactionReconciledAmountCents)) || 0
  return Math.abs(transaction.amountCents) - totalReconciledAmountCents
}

const EmptyBlock = ({ currency }: { currency?: string | null }) => (
  <Box
    flexDirection="column"
    gap="s8"
    width="s400"
    paddingX="s0"
    alignItems="center"
    textAlign="center"
    style={{ marginTop: '-20px' }}
  >
    <Icon.Large icon="file" color="grey600" />
    <Box flexDirection="column" gap="s4" alignItems="center">
      <Text.Header2>
        {i18n.t('banking.reconciliations.transactions_empty_state.title')}
      </Text.Header2>
      <Text.Normal color="grey800">
        {currency
          ? i18n.t('banking.reconciliations.transactions_empty_state.description_currency', {
              currency: currency.toUpperCase(),
            })
          : i18n.t('banking.reconciliations.transactions_empty_state.description')}
      </Text.Normal>
    </Box>
  </Box>
)

export const ReconciliationsTransactionsDataTable = ({
  onSelect,
  onRemove,
  isSelected,
  currency,
  disabled,
}: Props) => {
  const [filter, setFilter] = useState('')

  const { access } = useCurrentSession()
  const locale = access.locale

  const source = useReconciliationTransactionDataTable(
    [
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 0,
        render: (transaction) => {
          const pendingAmountCents = getPendingAmount(transaction)

          return (
            <Box flexDirection="row">
              <Checkbox
                disabled={disabled || transaction.fullyReconciledAt}
                checked={isSelected(transaction.id)}
                onChange={(event) => {
                  const reconciliation: ReconciliationItem = {
                    id: transaction.id,
                    description: transaction.description || '-',
                    amountCentsToReconcile: pendingAmountCents,
                    currency: transaction.currency || '',
                    date: transaction.bookingDate || '',
                    locale: locale,
                    type: transaction.amountCents > 0 ? 'debit' : 'credit',
                  }
                  if (event.target.checked) {
                    onSelect(reconciliation)
                  } else {
                    onRemove(reconciliation)
                  }
                }}
              />
            </Box>
          )
        },
      },
      {
        identifier: 'bookingDate',
        title: i18n.t('banking.attributes.booking_date'),
        kind: ProjectionKind.Custom,
        sortBy: 'bookingDate',
        template: '{{ bookingDate }}',
        width: 128,
        render: (transaction) => (
          <Box width="full" height="full" flexDirection="row" alignItems="center">
            <Text.Normal size="200">{dateBackendDateTimeToUI(transaction.bookingDate)}</Text.Normal>
          </Box>
        ),
      },
      {
        identifier: 'description',
        title: i18n.t('banking.attributes.description'),
        kind: ProjectionKind.Custom,
        template: '{{ description }}',
        render: (transaction) => {
          return (
            <Box>
              <Text.Normal ellipsis size="200">
                {transaction.description}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'pendingAmount',
        title: i18n.t('finance.invoices.list.fields.pendingAmount'),
        width: 120,
        kind: ProjectionKind.Custom,
        render: (transaction) => {
          const pendingAmountCents = getPendingAmount(transaction)
          return (
            <Box width="full" height="full" flexDirection="row" alignItems="center">
              <Text.Normal ellipsis weight="semibold" size="200" color="grey1000">
                {formattedAmount({
                  amountCents: Number(pendingAmountCents),
                  currency: transaction.currency ?? 'EUR',
                  locale,
                })}
              </Text.Normal>
            </Box>
          )
        },

        template: '{{ totalAmountCents }}',
      },
      {
        identifier: 'amount',
        width: 190,
        title: i18n.t('banking.attributes.amount'),
        kind: ProjectionKind.Custom,
        sortBy: 'amountCents',
        template: '{{ amountCents }} {{ currency }}',
        render: (transaction) => {
          return (
            <Box width="full" height="full" flexDirection="row" alignItems="center">
              <Text.Normal ellipsis weight="semibold" size="200">
                {formattedAmount({
                  amountCents: transaction.amountCents,
                  currency: transaction.currency,
                  locale: locale,
                })}
              </Text.Normal>
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        status: 'done',
        description: filter,
        currency,
      },
    }
  )

  const filters: DataTableFilter<BankingTransactionFilter>[] = [
    {
      identifier: 'transactionReconciledStatusFilter',
      title: i18n.t('banking.attributes.type'),
      path: 'reconciledStatus',
      default: true,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          value: ['pending'],
          hideSearch: true,
          operation: 'in',
          options: [
            {
              label: i18n.t('banking.reconciliations.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('banking.reconciliations.reconciled'),
              value: 'reconciled',
            },
          ],
        },
      ],
    },
    {
      identifier: 'transactionBookingDateFilter',
      title: i18n.t('banking.attributes.booking_date'),
      path: 'bookingDate',
      default: false,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DateTimePicker',
          operation: 'between',
          value: undefined,
        },
      ],
    },
  ]

  return (
    <Box width="full" maxHeight="s360">
      <DataTable
        disableEditColumns
        persistFilters={false}
        pagination={{
          itemsPerPage: 10,
          kind: 'offset',
        }}
        source={source}
        sort={{ path: 'bookingDate', direction: 'desc' }}
        filters={filters}
        disableExport
        search={{
          value: filter,
          onChange: (ev) => setFilter(ev.target.value),
          placeholder: i18n.t('banking.attributes.description'),
        }}
        emptyState={<EmptyBlock currency={currency} />}
      />
    </Box>
  )
}
